import { Delete, DragIndicator, Edit } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Avatar, Box, Card, CardContent, Chip, IconButton, Stack, Typography } from '@mui/material';
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useEffect, useMemo, useState } from 'react';
import { useDataProvider, useLocale, useNotify, useTranslate, Button } from 'react-admin';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { DashboardContent } from 'src/layouts/dashboard';
import { PermissionGuard } from 'src/routes/components';
import AuditLogDialog from 'src/pages/admin/components/AuditLog/AuditLogDialog';
import { Resources } from '../../resource';
import AddMarketButton from './components/AddMarketButton';
import EditMarketQuestionDialog from './components/EditMarketQuestionDialog';
import SearchMarketDialog from './components/SearchMarketDialog';
import { PredictionMarketItem } from './dataProvider';
import { Iconify } from 'src/components/iconify';
import './init';

const getOriginalQuestion = (market: PredictionMarketItem) => {
  const type = market.type as string;
  if (type === 'EVENT') {
    return market.title || '';
  }
  return market.question || '';
};

const getOriginalQuestionZh = (market: PredictionMarketItem) => {
  const type = market.type as string;
  if (type === 'EVENT') {
    return market.titleZh || '';
  }
  return market.questionZh || '';
};

const getMarketQuestion = (market?: PredictionMarketItem | null) => {
  const type = market?.type as string;
  if (type === 'EVENT') {
    return market?.customTitle || market?.title || '';
  }
  return market?.customQuestion || market?.question || '';
};

const getMarketQuestionZh = (market?: PredictionMarketItem | null) => {
  const type = market?.type as string;
  if (type === 'EVENT') {
    return market?.customTitleZh || market?.titleZh || '';
  }
  return market?.customQuestionZh || market?.questionZh || '';
};

function SortableMarketRow({
  id,
  item,
  index,
  onEdit,
  onRemove,
  locale,
}: {
  id: string;
  item: PredictionMarketItem;
  index: number;
  onEdit: (index: number) => void;
  onRemove: (index: number) => void;
  locale: string;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <Card variant="outlined">
        <CardContent sx={{ py: 2.5 }}>
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <IconButton
              size="small"
              sx={{ cursor: 'grab', color: 'text.disabled', mt: 0.5 }}
              {...attributes}
              {...listeners}
            >
              <DragIndicator fontSize="small" />
            </IconButton>
            <Avatar
              src={item.image || item.icon}
              alt={item.question}
              variant="rounded"
              sx={{ width: 48, height: 48 }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle2">
                {locale === 'en' ? getMarketQuestion(item) : getMarketQuestionZh(item)}
              </Typography>
              {item.slug ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {item.slug}
                </Typography>
              ) : null}
              {item.type ? (
                <Chip
                  label={item.type}
                  size="small"
                  color={item.type === 'EVENT' ? 'info' : 'default'}
                  sx={{ mt: 0.5 }}
                />
              ) : null}
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.5, display: 'block' }}
              >
                ID: {item.id}
              </Typography>
            </Box>
            <PermissionGuard resource="prediction-admin-events" action="update" fallback={<></>}>
              <IconButton color="info" onClick={() => onEdit(index)}>
                <Edit />
              </IconButton>
              <IconButton color="error" onClick={() => onRemove(index)}>
                <Delete />
              </IconButton>
            </PermissionGuard>
          </Stack>
        </CardContent>
      </Card>
    </div>
  );
}

const View = () => {
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const locale = useLocale();
  const notify = useNotify();
  const [markets, setMarkets] = useState<PredictionMarketItem[]>([]);
  const [initialMarkets, setInitialMarkets] = useState<PredictionMarketItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editingMarketIndex, setEditingMarketIndex] = useState<number | null>(null);
  const [updatingMarketQuestion, setUpdatingMarketQuestion] = useState(false);
  const [auditLogOpen, setAuditLogOpen] = useState(false);

  const marketKeys = useMemo(() => markets.map((market) => `${market.id}`), [markets]);

  const initialMarketKeys = useMemo(
    () => initialMarkets.map((market) => `${market.id}`),
    [initialMarkets]
  );

  const isChanged = JSON.stringify(marketKeys) !== JSON.stringify(initialMarketKeys);

  useEffect(() => {
    let active = true;

    const loadMarkets = async () => {
      setLoading(true);
      try {
        const response = await dataProvider.getList(Resources.predictionFeaturedMarkets, {
          filter: {},
          pagination: { page: 1, perPage: 50 },
          sort: { field: 'id', order: 'ASC' },
          meta: { acceptLanguage: 'en' },
        });
        const responseZh = await dataProvider.getList(Resources.predictionFeaturedMarkets, {
          filter: {},
          pagination: { page: 1, perPage: 50 },
          sort: { field: 'id', order: 'ASC' },
          meta: { acceptLanguage: 'zh' },
        });

        const marketsEn = response?.data ?? [];
        const marketsZh = responseZh?.data ?? [];

        const mergedMarkets = marketsEn.map((marketEn) => {
          const marketZh = marketsZh.find((m) => `${m.id}` === `${marketEn.id}`);
          const isEvent = (marketEn.type as string) === 'EVENT';
          if (isEvent) {
            return {
              ...marketEn,
              questionZh: marketZh?.question,
              customQuestionZh: marketZh?.customQuestion,
              titleZh: marketZh?.title,
              customTitleZh: marketZh?.customTitle,
            };
          }
          return {
            ...marketEn,
            questionZh: marketZh?.question,
            customQuestionZh: marketZh?.customQuestion,
          };
        });

        if (!active) return;

        setMarkets(mergedMarkets ?? []);
        setInitialMarkets(mergedMarkets ?? []);
      } catch (error: any) {
        if (!active) return;
        notify(error?.message || translate('module.featuredMarkets.loadError'), { type: 'error' });
      } finally {
        if (active) setLoading(false);
      }
    };

    loadMarkets();

    return () => {
      active = false;
    };
  }, [dataProvider, notify, translate]);

  const handleRemoveMarket = (index: number) => {
    setMarkets((currentMarkets) =>
      currentMarkets.filter((_, marketIndex) => marketIndex !== index)
    );
  };

  const handleSave = async () => {
    setSaving(true);

    try {
      await dataProvider.create(Resources.predictionFeaturedMarkets, {
        data: {
          items: markets.map((market, index) => ({
            type: (market.type as string) || 'MARKET',
            id: Number(market.id),
            index,
          })),
        },
      });

      setInitialMarkets(markets);
      notify(translate('module.featuredMarkets.saveSuccess'), { type: 'success' });
    } catch (error: any) {
      notify(error?.message || translate('module.featuredMarkets.saveError'), { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleOpenAddDialog = () => {
    setAddDialogOpen(true);
  };

  const handleOpenEditDialog = (index: number) => {
    setEditingMarketIndex(index);
  };

  const handleCloseAddDialog = () => {
    setAddDialogOpen(false);
  };

  const handleCancelEdit = () => {
    setEditingMarketIndex(null);
  };

  const handleCommitEdit = async (
    rawQuestion: string,
    rawQuestionZh: string,
  ) => {
    const nextQuestion = rawQuestion.trim();
    const nextQuestionZh = rawQuestionZh.trim();

    if (editingMarketIndex === null) return;

    const editingMarket = markets[editingMarketIndex];

    if (!editingMarket) {
      handleCancelEdit();
      return;
    }

    if (
      nextQuestion === getMarketQuestion(editingMarket) &&
      nextQuestionZh === getMarketQuestionZh(editingMarket)
    ) {
      handleCancelEdit();
      return;
    }

    setUpdatingMarketQuestion(true);

    const isEvent = (editingMarket.type as string) === 'EVENT';
    const updateResource = isEvent
      ? Resources.predictionFeaturedMarketsEvents
      : Resources.predictionFeaturedMarketsMarkets;

    const zhChanged = nextQuestionZh !== getMarketQuestionZh(editingMarket);
    const enChanged = nextQuestion !== getMarketQuestion(editingMarket);

    try {
      if (zhChanged) {
        await dataProvider.update(updateResource, {
          id: editingMarket.id,
          data: {
            question: nextQuestionZh,
            lang: 'zh',
          },
          previousData: editingMarket,
        });
      }
      if (enChanged) {
        await dataProvider.update(updateResource, {
          id: editingMarket.id,
          data: {
            question: nextQuestion,
            lang: 'en',
          },
          previousData: editingMarket,
        });
      }

      const applyEditedQuestion = (currentMarkets: PredictionMarketItem[]) => {
        if (isEvent) {
          return currentMarkets.map((market, index) =>
            index === editingMarketIndex
              ? {
                  ...market,
                  customTitle: nextQuestion,
                  customTitleZh: nextQuestionZh,
                }
              : market
          );
        }
        return currentMarkets.map((market, index) =>
          index === editingMarketIndex
            ? {
                ...market,
                customQuestion: nextQuestion,
                customQuestionZh: nextQuestionZh,
              }
            : market
        );
      };

      setMarkets(applyEditedQuestion);
      setInitialMarkets(applyEditedQuestion);
      handleCancelEdit();
      notify(translate('module.featuredMarkets.saveSuccess'), { type: 'success' });
    } catch (error: any) {
      notify(error?.message || translate('module.featuredMarkets.saveError'), { type: 'error' });
    } finally {
      setUpdatingMarketQuestion(false);
    }
  };

  const handleSelectMarket = (market: PredictionMarketItem) => {
    if (markets.some((item) => `${item.id}` === `${market.id}`)) {
      notify(translate('module.featuredMarkets.alreadyExists'), { type: 'warning' });
      return;
    }

    setMarkets((currentMarkets) => [...currentMarkets, market]);

    handleCloseAddDialog();
  };

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = markets.findIndex((item) => `${item.id}` === `${active.id}`);
      const newIndex = markets.findIndex((item) => `${item.id}` === `${over.id}`);
      setMarkets((prev) => arrayMove(prev, oldIndex, newIndex));
    }
  };

  return (
    <DashboardContent>
      <CustomBreadcrumbs
        heading={translate('resource.predictionFeaturedMarkets')}
        links={[
          { name: translate('resource.prediction') },
          { name: translate('resource.predictionFeaturedMarkets') },
        ]}
        action={
          <Stack direction="row" spacing={2}>
            <AddMarketButton onClick={handleOpenAddDialog} />
            <PermissionGuard resource="prediction-admin-events" action="update" fallback={<></>}>
              <LoadingButton
                variant="contained"
                color="primary"
                onClick={handleSave}
                loading={saving}
                disabled={loading || !isChanged}
              >
                {translate('actions.save')}
              </LoadingButton>
            </PermissionGuard>
            <PermissionGuard resource="audit-logs" action="read" fallback={<></>}>
              <Button
                variant="contained"
                size="medium"
                color="secondary"
                onClick={() => setAuditLogOpen(true)}
                label={translate('resource.auditLogs')}
              >
                <Iconify icon="ph:note" />
              </Button>
            </PermissionGuard>
          </Stack>
        }
        sx={{ mb: 3 }}
      />

      <Card sx={{ p: 3 }}>
        {loading ? (
          <Typography variant="body2" color="text.secondary">
            {translate('module.featuredMarkets.loading')}
          </Typography>
        ) : (
          <Stack spacing={2}>
            {markets.length === 0 ? (
              <Box
                sx={{
                  border: '1px dashed',
                  borderColor: 'divider',
                  borderRadius: 2,
                  p: 3,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {translate('module.featuredMarkets.empty')}
                </Typography>
              </Box>
            ) : null}

            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              modifiers={[restrictToVerticalAxis]}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={markets.map((item) => `${item.id}`)}
                strategy={verticalListSortingStrategy}
              >
                <Stack spacing={2}>
                  {markets.map((item, index) => (
                    <SortableMarketRow
                      key={`${item.id}-${index}`}
                      id={`${item.id}`}
                      item={item}
                      index={index}
                      onEdit={handleOpenEditDialog}
                      onRemove={handleRemoveMarket}
                      locale={locale}
                    />
                  ))}
                </Stack>
              </SortableContext>
            </DndContext>
          </Stack>
        )}
      </Card>

      <SearchMarketDialog
        open={addDialogOpen}
        selectedMarketIds={markets.map((market) => market.id)}
        onClose={handleCloseAddDialog}
        onSelectMarket={handleSelectMarket}
      />

      <EditMarketQuestionDialog
        open={editingMarketIndex !== null}
        type={
          editingMarketIndex !== null ? (markets[editingMarketIndex]?.type as string) : undefined
        }
        initialQuestion={
          editingMarketIndex !== null ? getMarketQuestion(markets[editingMarketIndex]) : ''
        }
        initialQuestionZh={
          editingMarketIndex !== null ? getMarketQuestionZh(markets[editingMarketIndex]) : ''
        }
        originalQuestion={
          editingMarketIndex !== null ? getOriginalQuestion(markets[editingMarketIndex]) : undefined
        }
        originalQuestionZh={
          editingMarketIndex !== null
            ? getOriginalQuestionZh(markets[editingMarketIndex])
            : undefined
        }
        loading={updatingMarketQuestion}
        onClose={handleCancelEdit}
        onConfirm={handleCommitEdit}
      />
      <AuditLogDialog
        open={auditLogOpen}
        onClose={() => setAuditLogOpen(false)}
        resource="prediction-custom-breaking"
        title={`${translate('resource.auditLogs')} - ${translate('resource.predictionFeaturedMarkets')}`}
      />
    </DashboardContent>
  );
};

export default View;
